import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { AlertTriangle, CheckCircle2, Package, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import type { CreateRequestResponse } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.api';
import { createPurchaseRequest } from '@/Shared/RequestDialog/components/EditDialog/api/HandRequest.api';
import LoadingOpacityDialog from '@/UI/LoadingOpacityDialog';

import { CheckItemDialog } from './components/CheckItemDialog';
import { ClientSection } from './components/ClientSection';
import { ConfirmationSection } from './components/ConfirmationSection';
import DiscountStatusBadge from './components/DiscountStatusBadge';
import RetailStatusBadge from './components/RetailStatusBadge';
import { SummarySection } from './components/SummarySection';
import WholeSaleStatusBadge from './components/WholeSaleStatusBadge';
import type { FinalStageProps } from './types';

function FinalStage({
  closeFn,
  isOpen,
  items,
  client,
  closeDialogFinalFn,
  handleLoadingStage,
  isLoading,
}: FinalStageProps) {
  // Memoize filtered items to prevent unnecessary recalculations
  const itemsInRequest = useMemo(
    () => items?.filter((x) => x?.Qty > 0),
    [items],
  );

  const [requestFinalData, setRequestFinalData] =
    useState<CreateRequestResponse>({
      success: false,
      message: '',
      requestId: '',
      total: 0,
      itemsCount: 0,
      status: undefined,
    });

  const [requestNotes, setRequestNotes] = useState('');
  const [requestError, setRequestError] = useState('');
  const [showCheckItemDialog, setShowCheckItemDialog] = useState(false);

  const handleRequestNotes = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setRequestNotes(event.target.value);
    },
    [],
  );

  const handleSetRequestFinalData = useCallback(
    (data: CreateRequestResponse) => {
      setRequestFinalData({
        success: data.success,
        message: data.success ? 'تم رفع الطلبية بنجاح' : data.message || '',
        requestId: data.requestId || '',
        total: data.total || 0,
        itemsCount: data.itemsCount || 0,
        status: data.status,
      });
    },
    [],
  );

  const queryClient = useQueryClient();

  const addRequestMutation = useMutation({
    mutationFn: createPurchaseRequest,
    onSuccess: async (data) => {
      // Check if the API response indicates success
      if (data.success) {
        handleSetRequestFinalData(data);
        // Batch invalidate queries to reduce renders
        await queryClient.invalidateQueries({
          queryKey: [
            'Offers-For-Request',
            'RequestsToday',
            'PendingRequests',
            'OffersToday',
            'OffersMonthly',
            'prepareOffers',
          ],
        });

        // Force refetch the Offers-For-Request query to ensure immediate update
        await queryClient.refetchQueries({
          queryKey: ['Offers-For-Request'],
        });

        handleLoadingStage(false, 'main');
        toast.success(
          '✅ 🎉 تم رفع الطلبية بنجاح 🚀 💫\nتم تحديث عروض الشراء اليوم',
        );
        setRequestError('');
      } else {
        // Handle API-level errors (when success is false)
        handleLoadingStage(false, 'main');
        const errorMessage = data.message || 'حدث خطأ أثناء رفع الطلبية';
        setRequestError(errorMessage);
        toast.error(errorMessage);

        // Ensure the CheckItemDialog is closed if open
        setShowCheckItemDialog(false);

        // Reset the request data to ensure non-success state
        setRequestFinalData({
          success: false,
          message: errorMessage,
          requestId: '',
          total: 0,
          itemsCount: 0,
          status: undefined,
        });
      }
    },
    onError: (
      err: AxiosError<{
        message?: string;
        messages?: Array<{ message: string }>;
      }>,
    ) => {
      handleLoadingStage(false, 'main');

      // Enhanced error message extraction
      let errorMessage = 'حدث خطأ أثناء رفع الطلبية';

      if (err.response) {
        // Server responded with error status
        const status = err.response.status;
        const responseData = err.response.data;

        // Try to get error message from response
        if (responseData?.messages && responseData.messages.length > 0) {
          errorMessage = responseData.messages[0].message;
        } else if (responseData?.message) {
          // Translate common server messages to user-friendly Arabic
          const serverMessage = responseData.message;
          if (serverMessage.toLowerCase().includes('client not found')) {
            errorMessage =
              'العميل المحدد غير موجود. يرجى التحقق من بيانات العميل';
          } else if (serverMessage.toLowerCase().includes('offer not found')) {
            errorMessage =
              'العرض المحدد غير موجود. يرجى التحقق من بيانات العرض';
          } else if (serverMessage.toLowerCase().includes('item not found')) {
            errorMessage = 'أحد الأصناف المحددة غير موجود. يرجى مراجعة الأصناف';
          } else if (serverMessage.toLowerCase().includes('invalid')) {
            errorMessage = 'بيانات غير صحيحة. يرجى مراجعة المعلومات المدخلة';
          } else if (
            serverMessage.toLowerCase().includes('permission') ||
            serverMessage.toLowerCase().includes('unauthorized')
          ) {
            errorMessage = 'ليس لديك صلاحية لتنفيذ هذا الإجراء';
          } else {
            // For other server messages, show them as-is but add Arabic context
            errorMessage = `خطأ من الخادم: ${serverMessage}`;
          }
        } else {
          // Provide user-friendly messages based on status codes
          switch (status) {
            case 400:
              errorMessage =
                'بيانات الطلبية غير صحيحة. يرجى التحقق من البيانات المدخلة';
              break;
            case 401:
              errorMessage = 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى';
              break;
            case 403:
              errorMessage = 'ليس لديك صلاحية لإنشاء طلبات الشراء';
              break;
            case 404:
              errorMessage =
                'لم يتم العثور على الخدمة المطلوبة. يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني';
              break;
            case 409:
              errorMessage = 'تعارض في البيانات. قد تكون الطلبية موجودة مسبقاً';
              break;
            case 422:
              errorMessage = 'بيانات الطلبية غير مكتملة أو غير صحيحة';
              break;
            case 429:
              errorMessage =
                'تم تجاوز الحد المسموح من الطلبات. يرجى المحاولة بعد قليل';
              break;
            case 500:
              errorMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى';
              break;
            case 502:
            case 503:
            case 504:
              errorMessage = 'الخدمة غير متاحة حالياً. يرجى المحاولة بعد قليل';
              break;
            default:
              errorMessage = `حدث خطأ غير متوقع (${status}). يرجى المحاولة مرة أخرى`;
          }
        }
      } else if (err.request) {
        // Request was made but no response received
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          errorMessage =
            'انتهت مهلة الاتصال. يرجى التحقق من الاتصال بالإنترنت والمحاولة مرة أخرى';
        } else if (err.code === 'ERR_NETWORK') {
          errorMessage = 'خطأ في الشبكة. يرجى التحقق من الاتصال بالإنترنت';
        } else {
          errorMessage =
            'لا يمكن الاتصال بالخادم. يرجى التحقق من الاتصال بالإنترنت';
        }
      } else {
        // Something else happened
        errorMessage = err.message || 'حدث خطأ غير متوقع';
      }

      console.error('Request Error Details:', {
        message: err.message,
        status: err.response?.status,
        code: err.code,
        serverMessage: err.response?.data?.message,
        translatedMessage: errorMessage,
        fullResponseData: err.response?.data,
      });

      setRequestError(errorMessage);
      toast.error(errorMessage);

      // Ensure the CheckItemDialog is closed if open
      setShowCheckItemDialog(false);

      // Reset the request data to ensure non-success state
      setRequestFinalData({
        success: false,
        message: errorMessage,
        requestId: '',
        total: 0,
        itemsCount: 0,
        status: undefined,
      });
    },
  });

  // Memoize the finishItemMap to avoid recreating on every render
  const finishItemMap = useMemo(
    () =>
      itemsInRequest.map((item) => ({
        BNO: '99',
        Bonus: 0,
        Discount: item.DiscountEdit,
        DiscountPro: 0,
        HealthyItemID: item.HealthyItemID ?? 0,
        DiscountStatus: item.DiscountStatus,
        ExpireDate: dayjs().format('MM-YYYY'),
        Price: item.Price,
        Qty: item.Qty,
        RetailStatus: item.RetailStatus,
        Tax: 0,
        WholeSaleStatus: item.WholeSaleSalesStatus,
        UnderRequest: item.totalUnderRequests,
      })),
    [itemsInRequest],
  );

  const handleSubmitRequest = useCallback(async () => {
    handleLoadingStage(true, 'upload');
    await addRequestMutation.mutateAsync({
      items: finishItemMap,
      clientID: client.ClientOID,
      offerID: client._id,
      notes: requestNotes,
    });
  }, [
    addRequestMutation,
    client.ClientOID,
    client._id,
    finishItemMap,
    handleLoadingStage,
    requestNotes,
  ]);

  const handleConfirmButtonClick = useCallback(() => {
    setShowCheckItemDialog(true);
  }, []);

  const resetAllState = useCallback(() => {
    setRequestFinalData({
      success: false,
      message: '',
      requestId: '',
      total: 0,
      itemsCount: 0,
      status: undefined,
    });
    setRequestNotes('');
    setRequestError('');
    setShowCheckItemDialog(false);
  }, []);

  const handleCloseDialog = useCallback(() => {
    if (requestFinalData?.success) {
      resetAllState();
      closeFn();
      closeDialogFinalFn();
    } else {
      closeFn();
    }
  }, [requestFinalData?.success, closeFn, closeDialogFinalFn, resetAllState]);

  // Check if an item has issues that require attention
  const hasItemIssues = useCallback((item: IOneItemInPrepareForRequest) => {
    return (
      item.DiscountStatus === 'bad' ||
      item.RetailStatus === 'overStock' ||
      item.RetailStatus === 'canTransfer' ||
      item.WholeSaleSalesStatus === 'overStock' ||
      item.WholeSaleSalesStatus === 'canTransfer' ||
      (item.totalUnderRequests && item.totalUnderRequests > 0)
    );
  }, []);

  const handleErrorDialogClose = useCallback(() => {
    setRequestError('');
    // Also reset the request data to ensure clean state
    setRequestFinalData({
      success: false,
      message: '',
      requestId: '',
      total: 0,
      itemsCount: 0,
      status: undefined,
    });
  }, []);

  // Reset everything when component unmounts or when isOpen becomes false
  useEffect(() => {
    if (!isOpen) {
      // Only reset state if there's no error to display
      if (!requestError) {
        resetAllState();
      }
    }

    // Cleanup function only on unmount, not on every dependency change
    return () => {
      // Only reset if component is actually unmounting
    };
  }, [isOpen, requestError, resetAllState]);

  const handleDialogChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        // Only reset state if there's no error to display
        if (!requestError) {
          resetAllState();
        }
      }
    },
    [resetAllState, requestError],
  );

  // Memoize the items with issues count
  const itemsWithIssuesCount = useMemo(
    () => itemsInRequest.filter(hasItemIssues).length,
    [itemsInRequest, hasItemIssues],
  );

  // Calculate percentage of items with issues only when needed
  const itemIssuesPercentage = useMemo(() => {
    if (itemsWithIssuesCount === 0 || itemsInRequest.length === 0) return 0;
    return Math.round((itemsWithIssuesCount / itemsInRequest.length) * 100);
  }, [itemsWithIssuesCount, itemsInRequest.length]);

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent
        hiddenCloseIcon
        className="max-w-[90vw] p-0 pr-0.5 md:max-w-[80vw]"
        size="5xl"
        overlayClass="custom-dialog-overlay"
        aria-describedby="final-stage-dialog-description"
      >
        <DialogTitle className="w-full border-b border-primary/10 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-3 dark:border-primary/15">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
                <CheckCircle2 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">
                  {requestFinalData?.success
                    ? 'تم رفع الطلبية بنجاح'
                    : 'رفع طلبية الشراء'}
                </h2>
                <DialogDescription
                  id="final-stage-dialog-description"
                  className="text-sm text-muted-foreground"
                >
                  تأكيد طلبية الشراء من المورد
                </DialogDescription>
              </div>
            </div>

            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseDialog}
                className="h-8 w-8 rounded-full hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogTitle>

        <div className="bg-gradient-to-b from-background/50 to-background p-3">
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="col-span-6">
                <div className="rounded-md border border-primary/10 bg-card p-3 shadow-sm">
                  <SectionWrapper
                    title="معلومات العميل"
                    description="تفاصيل العميل وبيانات الاتصال"
                  >
                    <ClientSection client={client} />
                  </SectionWrapper>
                </div>
              </div>

              <div className="col-span-6">
                <div className="rounded-md border border-primary/10 bg-card p-3 shadow-sm">
                  <SectionWrapper
                    title="ملخص الطلبية"
                    description="تفاصيل وملاحظات الطلبية"
                  >
                    <SummarySection
                      items={items}
                      itemsInRequest={itemsInRequest}
                      requestNotes={requestNotes}
                      handleRequestNotes={handleRequestNotes}
                      isDisabled={requestFinalData?.success}
                    />
                  </SectionWrapper>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="col-span-12">
                <div className="rounded-md border border-primary/10 bg-card p-3 shadow-sm">
                  <SectionWrapper
                    title="المنتجات المطلوبة"
                    description="تفاصيل المنتجات في الطلبية"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary shadow-sm">
                          <Package className="h-3 w-3" />
                          إجمالي الأصناف: {itemsInRequest.length}
                        </span>

                        {itemsWithIssuesCount > 0 && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-700 shadow-sm dark:bg-red-900/30 dark:text-red-400">
                            <AlertTriangle className="h-3 w-3" />
                            أصناف تحتاج مراجعة: {itemsWithIssuesCount}
                            <span className="rounded-md bg-red-200 px-1 py-0.5 text-[8px] font-bold dark:bg-red-800/40">
                              {itemIssuesPercentage}%
                            </span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="h-[180px] overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm">
                          <tr className="border-b border-border/40 shadow-sm">
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              الصنف
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              الكمية
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              السعر
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              الخصم
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              حالة الخصم
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              الطلبات تحت المعالجة
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              حالة التجزئة
                            </th>
                            <th className="p-1 text-right font-medium text-muted-foreground">
                              حالة الجملة
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsInRequest.map((item, index) => {
                            const itemHasIssues = hasItemIssues(item);
                            return (
                              <tr
                                key={`${item.HealthyItemID}-${index}-main-table`}
                                className={cn(
                                  'border-b border-border/40 transition-colors duration-200 last:border-0',
                                  itemHasIssues
                                    ? 'bg-red-50/70 hover:bg-red-100/80 dark:bg-red-900/10 dark:hover:bg-red-900/20'
                                    : 'hover:bg-muted/50',
                                )}
                              >
                                <td
                                  className={cn(
                                    'p-1',
                                    itemHasIssues &&
                                      'font-medium text-red-700 dark:text-red-400',
                                  )}
                                >
                                  {item.itemName}
                                </td>
                                <td className="p-1">{item.Qty}</td>
                                <td className="p-1">{item.Price}</td>
                                <td
                                  className={cn(
                                    'p-1 text-center align-middle',
                                    item.DiscountStatus === 'bad' &&
                                      'font-semibold text-red-600 dark:text-red-400',
                                  )}
                                >
                                  {item.DiscountEdit}%
                                </td>
                                <td className="p-1 text-center align-middle">
                                  <DiscountStatusBadge
                                    discountStatus={item.DiscountStatus}
                                  />
                                </td>
                                <td
                                  className={cn(
                                    'p-1',
                                    item.totalUnderRequests > 0 &&
                                      'font-semibold text-blue-600 dark:text-blue-400',
                                  )}
                                >
                                  {item.totalUnderRequests}
                                </td>
                                <td className="p-1 text-center align-middle">
                                  <RetailStatusBadge
                                    status={item.RetailStatus}
                                    diff={item.RetailDiff}
                                  />
                                </td>
                                <td className="p-1 text-center align-middle">
                                  <WholeSaleStatusBadge
                                    status={item.WholeSaleSalesStatus}
                                    diff={item.WholeSaleDiff}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </SectionWrapper>
                </div>
              </div>

              <div className="col-span-12">
                <div className="rounded-md border border-primary/10 bg-card p-3 shadow-sm">
                  <ConfirmationSection
                    requestDone={requestFinalData.success}
                    handleSubmit={handleConfirmButtonClick}
                    requestData={{
                      success: requestFinalData.success,
                      message: requestFinalData.message || '',
                      requestId: requestFinalData.requestId || '',
                      total: requestFinalData.total || 0,
                      itemsCount: requestFinalData.itemsCount || 0,
                      status: requestFinalData.status,
                    }}
                    clientName={client.ClientName}
                    error={requestError}
                    onViewRequest={() => {
                      resetAllState();
                      closeFn();
                      closeDialogFinalFn();
                    }}
                    onErrorDialogClose={handleErrorDialogClose}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading && <LoadingOpacityDialog />}
      </DialogContent>

      <CheckItemDialog
        isOpen={showCheckItemDialog}
        onOpenChange={setShowCheckItemDialog}
        items={itemsInRequest}
        onConfirm={() => {
          setShowCheckItemDialog(false);
          handleSubmitRequest();
        }}
        onCancel={() => setShowCheckItemDialog(false)}
      />
    </Dialog>
  );
}

export default FinalStage;
